// chats-layer/composables/useChats.ts
import { computed, type MaybeRefOrGetter, toValue, watch } from 'vue'

import { v7 as uuidv7 } from 'uuid'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useLocation } from '@common/location-layer/composables/useLocation'

import { useGroupsUIStore } from '@communities/groups-layer/stores/groups.store'
import { useUpload } from '@communities/groups-layer/composables/useMediaUpload'

import { useChatsQueryStore } from '../stores/chats.query-store'
import { useChatsUIStore } from '../stores/chats.ui-store'
import { type ConversationEventType, MessageStatus } from '../types/chats.enums'
import { getChatParticipantId, normalizeConversationRecipient, suggestionToRecipientApi } from '../utils/conversation.transform'
import { useChatParticipant } from './useChatParticipant'

import type { RecordedVoiceMessage } from '../types/chat-message-editor.types'
import type {
  IConversation,
  IConversationPaginationParams,
  IConversationSuggestion,
  ICreateConversationInput,
  ICreateConversationOptions,
  IMessage,
  ISendMessageInput,
  ISuggestionPaginationParams,
} from '../types/conversation.types'

/**
 * Chats data layer.
 *
 * Consumed by the app shell (`GKRootContainer`), whose own template mounts the notification
 * provider, so this composable must not resolve any provider-scoped dependency during setup —
 * an `inject()` miss here throws out of setup and Nuxt turns it into an SSR 500 on every route.
 * Create-conversation failures are reported to `chats.ui-store` — a plain Pinia write, safe from
 * the shell and from SSR — and rethrown. `ChatErrorToaster`, mounted inside the provider, turns
 * them into a toast, so no caller has to wire up messaging of its own.
 */
export const useChats = () => {
  const queryStore = useChatsQueryStore()
  const uiStore = useChatsUIStore()
  const groupsStore = useGroupsUIStore()
  const { locationId: clientPortalLocationId } = useLocation()
  const { store: authStore, ensureAuthenticated } = useAuth()
  const { getCurrentUserId, getConversationRecipient } = useChatParticipant()
  const { isGokollab } = useGlobal()

  const locationId = computed(() => {
    if (toValue(isGokollab)) {
      return toValue(clientPortalLocationId)
    }

    return groupsStore.currentGroup?.locationId || toValue(clientPortalLocationId)
  })

  // ===== QUERY FUNCTIONS =====

  /**
   * Fetch conversations with infinite scroll
   */
  const fetchConversations = (params?: MaybeRefOrGetter<Partial<IConversationPaginationParams> | undefined>) => {
    return queryStore.fetchInfiniteConversationsQuery(locationId, params)
  }

  /**
   * Fetch a single conversation
   */
  const fetchConversation = (conversationId: MaybeRefOrGetter<string | undefined>) => {
    return queryStore.fetchConversationQuery(locationId, conversationId)
  }

  /**
   * Fetch messages for a conversation with infinite scroll
   */
  const fetchMessages = (conversationId: MaybeRefOrGetter<string | undefined>) => {
    return queryStore.fetchInfiniteMessagesQuery(locationId, conversationId)
  }

  /**
   * Search for conversation suggestions (members to start a new chat)
   */
  const searchSuggestions = (
    searchQuery: MaybeRefOrGetter<string>,
    params?: MaybeRefOrGetter<Partial<ISuggestionPaginationParams> | undefined>
  ) => {
    return queryStore.searchInfiniteSuggestionsQuery(locationId, searchQuery, params)
  }

  /**
   * Fetch unread conversation count
   */
  const fetchUnreadCount = (enabled?: MaybeRefOrGetter<boolean>) => {
    return queryStore.fetchUnreadCountQuery(locationId, enabled)
  }

  // ===== MUTATION FUNCTIONS =====

  /**
   * Create a new conversation
   */
  const createConversation = async (input: ICreateConversationInput): Promise<IConversation> => {
    const locId = toValue(locationId)
    if (!locId) {
      throw new Error('Location ID is required')
    }
    return queryStore.createConversationMutation.mutateAsync({ locationId: locId, input })
  }

  /**
   * Build a valid send-message input from conversation and recipient ids.
   */
  const buildSendMessageInput = (input: {
    conversationId?: string | null
    recipientId?: string | null
    text?: string
    media?: ISendMessageInput['media']
  }): ISendMessageInput | null => {
    const conversationId = input.conversationId?.trim()
    const recipientId = input.recipientId?.trim()

    if (!conversationId || !recipientId) {
      return null
    }

    return {
      conversationId,
      recipientId,
      ...(input.text !== undefined ? { text: input.text } : {}),
      ...(input.media ? { media: input.media } : {}),
    }
  }

  /**
   * Send a message to a conversation
   */
  const sendMessage = async (input: ISendMessageInput) => {
    const locId = toValue(locationId)
    if (!locId) {
      throw new Error('Location ID is required')
    }

    const isGokollabMode = toValue(isGokollab)
    const isUserV2 = !isGokollabMode
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      throw new Error('Sender ID is required')
    }

    const recipientId = input.recipientId?.trim()
    if (!recipientId) {
      throw new Error('Recipient ID is required')
    }

    const uniqueRefId = uuidv7()
    const optimisticMessage = {
      _id: uniqueRefId,
      conversationId: input.conversationId,
      sender: currentUserId,
      content: {
        text: input.text,
        media: input.media,
      },
      uniqueRefId,
      createdAt: new Date().toISOString(),
      isOptimistic: true,
      status: MessageStatus.SENDING,
    }

    const isVoiceNoteMedia =
      input.media?.type === 'audio' &&
      (input.media.kind === 'voice_note' ||
        (input.media.waveformPeaks?.length ?? 0) > 0 ||
        (!!input.media.licenseId && (input.media.durationSec ?? 0) > 0))

    const mediaPayload = input.media
      ? {
          type: input.media.type,
          url: input.media.url,
          licenseId: input.media.licenseId,
          name: input.media.fileName || input.media.url,
          width: input.media.width,
          height: input.media.height,
          // Voice-note metadata (audio only). `kind` is sent explicitly; the
          // backend also derives it from `waveformPeaks`.
          ...(isVoiceNoteMedia
            ? {
                kind: 'voice_note' as const,
                durationSec: input.media.durationSec,
                waveformPeaks: input.media.waveformPeaks,
              }
            : {}),
        }
      : undefined

    return queryStore.sendMessageMutation.mutateAsync({
      locationId: locId,
      payload: {
        conversationId: input.conversationId,
        sender: currentUserId,
        recipient: recipientId,
        text: input.text,
        media: mediaPayload,
        uniqueRefId,
        refId: uniqueRefId,
        isGokollab: isGokollabMode,
        isUserV2,
      },
      optimisticMessage,
    })
  }

  /**
   * Upload a recorded voice clip through the same signed-URL path as document
   * attachments (no DRM). Chats are not group-scoped, so no groupId is passed:
   * the asset is stored at location level under the chat-media folder
   * (`{locationId}/chat-media/...`). Resolves the public URL, or rejects on failure.
   */
  const uploadVoiceFile = (file: File, locId: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const { loadFiles, uploadResponse, uploading } = useUpload()
      const stop = watch([uploading, uploadResponse], ([isUploadingNow, response], previousState) => {
        const wasUploading = previousState?.[0] ?? false
        if (!isUploadingNow && response) {
          stop()
          resolve(response.url)
        } else if (wasUploading && !isUploadingNow && !response) {
          // useUpload can fail asynchronously without rejecting.
          stop()
          reject(new Error('Voice upload failed'))
        }
      })

      const run = async () => {
        try {
          await loadFiles(file, 'chat-media', locId)
        } catch (error) {
          stop()
          reject(error instanceof Error ? error : new Error('Voice upload failed'))
        }
      }
      void run()
    })

  /**
   * Send a recorded voice note.
   *
   * NOTE — why this does NOT use the one-shot `sendMessageMutation` like text /
   * image / video. This is intentional, not a missed abstraction:
   *
   * Text/image/video are already uploaded by the editor BEFORE `sendMessage`
   * runs (DRM / `useUpload` resolve the server URL upstream), so `media.url` is
   * final by send time. `sendMessageMutation.onMutate` can therefore insert the
   * optimistic bubble AND emit the socket atomically in a single step.
   *
   * Voice is different: the editor emits `voiceMessage` with the raw blob and
   * returns immediately (WhatsApp UX — the clip must drop into history and play
   * the local recording WHILE it uploads). That forces three ordered phases the
   * one-shot mutation cannot express, because the emit must wait for the upload
   * but the bubble must appear before it:
   *   1. insert optimistic bubble now (local object URL + waveform, "sending")
   *   2. upload in the background
   *   3. swap blob URL -> server URL, THEN emit over the socket
   *
   * These reuse the SAME optimistic primitives as the mutation — the cache shape
   * (`insertOptimisticMessage` calls the identical `updateMessagesCache` used in
   * `onMutate`) and the same `uniqueRefId` ACK reconciliation via
   * `handleMessageSent` ("sent"). A failure flips the bubble to "failed" but
   * keeps the local blob so it still renders/plays (deliberately no rollback,
   * unlike the mutation's `onError`).
   */
  const sendVoiceMessage = async (input: { conversationId: string; recipientId?: string | null } & RecordedVoiceMessage) => {
    const locId = toValue(locationId)
    if (!locId) throw new Error('Location ID is required')
    const sender = getCurrentUserId()
    if (!sender) throw new Error('Sender ID is required')
    const recipientId = input.recipientId?.trim()
    if (!recipientId) throw new Error('Recipient ID is required')

    const isGokollabMode = toValue(isGokollab)
    const isUserV2 = !isGokollabMode
    const uniqueRefId = uuidv7()

    const optimisticMessage: IMessage = {
      _id: uniqueRefId,
      conversationId: input.conversationId,
      sender,
      content: {
        media: {
          type: 'audio',
          kind: 'voice_note',
          url: input.localUrl,
          fileName: input.fileName,
          mimeType: input.mimeType,
          durationSec: input.durationSec,
          waveformPeaks: input.waveformPeaks,
        },
      },
      uniqueRefId,
      createdAt: new Date().toISOString(),
      isOptimistic: true,
      status: MessageStatus.SENDING,
    } as IMessage

    // Show the bubble in history immediately, like every other message.
    queryStore.insertOptimisticMessage(locId, input.conversationId, optimisticMessage)

    try {
      const serverUrl = await uploadVoiceFile(input.file, locId)

      // Swap the local blob URL for the uploaded URL, then release the blob.
      queryStore.updateOptimisticMessage(locId, input.conversationId, uniqueRefId, message => ({
        ...message,
        content: {
          ...message.content,
          media: message.content?.media ? { ...message.content.media, url: serverUrl } : message.content?.media,
        },
      }))
      if (input.localUrl.startsWith('blob:')) URL.revokeObjectURL(input.localUrl)

      queryStore.emitMessage(locId, {
        conversationId: input.conversationId,
        sender,
        recipient: recipientId,
        media: {
          type: 'audio',
          kind: 'voice_note',
          url: serverUrl,
          name: input.fileName,
          durationSec: input.durationSec,
          waveformPeaks: input.waveformPeaks,
        },
        uniqueRefId,
        refId: uniqueRefId,
        isGokollab: isGokollabMode,
        isUserV2,
      })
    } catch {
      // Keep the local blob so the failed bubble still renders/plays.
      queryStore.updateOptimisticMessage(locId, input.conversationId, uniqueRefId, message => ({
        ...message,
        status: MessageStatus.FAILED,
        isOptimistic: false,
      }))
    }
  }

  /**
   * Mark a conversation as read
   */
  const markAsRead = async (conversationId: string) => {
    const locId = toValue(locationId)
    if (!locId) {
      throw new Error('Location ID is required')
    }
    return queryStore.markAsReadMutation.mutateAsync({ locationId: locId, conversationId })
  }

  /**
   * Block a conversation
   */
  const blockConversation = async (conversationId: string) => {
    const locId = toValue(locationId)
    if (!locId) {
      throw new Error('Location ID is required')
    }
    return queryStore.blockConversationMutation.mutateAsync({ locationId: locId, conversationId })
  }

  /**
   * Unblock a conversation
   */
  const unblockConversation = async (conversationId: string) => {
    const locId = toValue(locationId)
    if (!locId) {
      throw new Error('Location ID is required')
    }
    return queryStore.unblockConversationMutation.mutateAsync({ locationId: locId, conversationId })
  }

  /**
   * Delete conversation history
   */
  const deleteConversation = async (conversationId: string) => {
    const locId = toValue(locationId)
    if (!locId) {
      throw new Error('Location ID is required')
    }
    return queryStore.deleteConversationMutation.mutateAsync({ locationId: locId, conversationId })
  }

  /**
   * Send conversation-level events (typing, notTyping)
   */
  const sendConversationEvent = async (conversation: IConversation, eventType: ConversationEventType) => {
    const locId = toValue(locationId)
    if (!locId) {
      throw new Error('Location ID is required')
    }

    const currentUserId = getCurrentUserId()
    const recipientId = getConversationRecipient(conversation)?.recipientId?.trim() ?? ''
    if (!currentUserId || !recipientId) {
      return
    }

    return queryStore.sendConversationEvent(locId, {
      eventType,
      conversationId: conversation._id,
      contactId: currentUserId,
      recipientId,
      sender: currentUserId,
    })
  }

  /**
   * Start a new chat with a contact (from suggestion)
   * Creates the conversation and returns it with recipient info
   */
  const startChatWithContact = async (contact: IConversationSuggestion, options?: ICreateConversationOptions): Promise<IConversation> => {
    if (!ensureAuthenticated()) {
      throw new Error('Authentication required to start a direct chat')
    }

    const resolvedLocId = options?.locationId || toValue(locationId)
    if (!resolvedLocId) {
      throw new Error('Location ID is required')
    }

    const recipientApi = suggestionToRecipientApi(contact)
    const input: ICreateConversationInput = {
      members: [contact.contactId],
      type: 'DM',
      skipFetchingContactId: options?.skipFetchingContactId ?? true,
      recipient: recipientApi,
    }

    let conversation: IConversation
    try {
      conversation = await queryStore.createConversationMutation.mutateAsync({
        locationId: resolvedLocId,
        input,
        groupId: options?.groupId,
      })
    } catch (error: unknown) {
      uiStore.reportCreateConversationError(error)
      throw error
    }

    if (conversation.recepients?.length) {
      return conversation
    }

    // Defensive fallback: keep UI usable if upstream APIs return an unhydrated DM payload.
    return {
      ...conversation,
      recepients: [normalizeConversationRecipient(recipientApi)],
    }
  }

  /**
   * Open existing DM with a contact (if cached) or create a new one, then open chat window.
   */
  const openOrCreateDirectChatWithContact = async (
    contact: IConversationSuggestion,
    options?: ICreateConversationOptions
  ): Promise<IConversation> => {
    if (!ensureAuthenticated()) {
      throw new Error('Authentication required to open a direct chat')
    }

    const lookupRecipientId = getChatParticipantId(contact)
    if (!lookupRecipientId) {
      throw new Error('Recipient ID is required')
    }

    const openConversation = uiStore.activeChatWindows
      .map(window => window.conversation)
      .find(conversation => {
        const isDirectConversation = !conversation.type || conversation.type === 'DM'
        return isDirectConversation && conversation.recepients?.some(recipient => recipient.recipientId === lookupRecipientId)
      })
    if (openConversation) {
      selectConversationAndOpenWindow(openConversation)
      return openConversation
    }

    const resolvedLocId = options?.locationId || toValue(locationId)
    if (resolvedLocId) {
      const existingConversation = queryStore.findCachedDirectConversationByRecipientId(resolvedLocId, lookupRecipientId)

      if (existingConversation) {
        selectConversationAndOpenWindow(existingConversation)
        return existingConversation
      }
    }

    const conversation = await startChatWithContact(contact, options)
    selectConversationAndOpenWindow(conversation)

    return conversation
  }

  // ===== UI ACTIONS =====

  /**
   * Open a chat with a specific conversation
   */
  const openChat = (conversation: IConversation) => {
    uiStore.openPanel()
    uiStore.selectConversation(conversation._id)
  }

  /**
   * Open a chat window (for multi-window mode)
   */
  const openChatWindow = (conversation: IConversation) => {
    uiStore.openChatWindow(conversation)
  }

  /**
   * Select conversation from list, open its window and mark as read.
   */
  const selectConversationAndOpenWindow = (conversation: IConversation) => {
    if (conversation._id) {
      uiStore.selectConversation(conversation._id)
    }

    uiStore.openChatWindow(conversation)

    if (conversation._id) {
      void (async () => {
        try {
          await markAsRead(conversation._id)
        } catch (error) {
          console.error('Failed to mark conversation as read:', error)
        }
      })()
    }
  }

  /**
   * Open a DM chat window given only a conversation id (desktop floating window).
   *
   * Resolves the id to a full `IConversation` via `ensureConversation` — a
   * promise-returning, cache-first client method (not the reactive `useQuery`
   * wrapper) — then opens the window. Constructing `useChats` needs a valid inject
   * context; once built, this method can be awaited from anywhere (e.g. a push
   * notification click handler or boot-time deep link, reached via
   * `nuxtApp.runWithContext`). See `ensureConversation` for the full rationale.
   *
   * Note: floating windows are desktop-only (`ChatContainer` renders nothing on
   * mobile). Mobile callers should navigate to the `communities-conversations-conversationId`
   * route instead.
   */
  const openConversationById = async (conversationId: string): Promise<void> => {
    const locId = locationId.value
    const convId = conversationId?.trim()
    if (!locId || !convId) return

    try {
      const conversation = await queryStore.ensureConversation(locId, convId)
      selectConversationAndOpenWindow(conversation)
    } catch (error) {
      console.error('Failed to open conversation by id:', error)
      throw error
    }
  }

  // ===== COMPUTED =====

  const isSendingMessage = computed(() => queryStore.sendMessageMutation.isPending.value)
  const isCreatingConversation = computed(() => queryStore.createConversationMutation.isPending.value)

  return {
    // UI Store (for panel state management)
    store: uiStore,

    // Query functions
    fetchConversations,
    fetchConversation,
    fetchMessages,
    fetchUnreadCount,
    searchSuggestions,

    // Mutation functions
    buildSendMessageInput,
    createConversation,
    sendMessage,
    sendVoiceMessage,
    markAsRead,
    blockConversation,
    unblockConversation,
    deleteConversation,
    sendConversationEvent,
    startChatWithContact,
    openOrCreateDirectChatWithContact,

    // UI actions
    openChat,
    openChatWindow,
    selectConversationAndOpenWindow,
    openConversationById,

    // Computed states
    isSendingMessage,
    isCreatingConversation,

    // Query keys for manual invalidation
    queryKeys: queryStore.queryKeys,
  }
}
